import { computed, ref } from 'vue'
import * as apiRequest from '@shared/api-endpoints'
import env from '@shared/env.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useForm from '@shared/composables/useForm.js'

// Submits the insurer picked on the insurance selection screen. Both variants
// of the insuranceSelectionRedesign experiment use it, so the rules live once:
// a pick is required, it is saved to the profile (or loaded before signup),
// insurers without DiGA support stop at a warning, and the funnel moves on.
// Each screen keeps its own search and list and passes its current pick;
// fieldName is the form field that carries the selection error.
export default function useInsuranceSubmission({
  selectedInsurance,
  fieldName,
  navigateNext,
}) {
  const authStore = useAuthStore()
  const corporateStore = useCorporateStore()

  const form = ref({ [fieldName]: '' })
  const formConfig = ref({ [fieldName]: {} })
  const showGenericError = ref(false)
  const digaNotSupportedCorporateId = ref(null)

  // Only shown while the diga-incompatible insurance stays selected — picking a
  // different insurance resets the warning automatically.
  const showDigaNotSupported = computed(
    () =>
      selectedInsurance.value?.id != null &&
      selectedInsurance.value.id === digaNotSupportedCorporateId.value,
  )

  const { fieldAttrs, submitAttrs, submit, addErrors } = useForm(
    form,
    formConfig,
    {
      onSubmit: async () => {
        showGenericError.value = false

        if (selectedInsurance.value == null) {
          addErrors(fieldName, 'eu__insurance_search__selection_required_error')
          const err = new Error('Selection required')
          err.code = 'SELECTION_REQUIRED'
          throw err
        }

        const corporateId = selectedInsurance.value.id
        if (typeof corporateId !== 'number') {
          throw new Error('Selected insurance is missing corporate id')
        }

        if (authStore.isAuthenticated) {
          await apiRequest.setUserProfile({ corporateId })
        } else {
          await apiRequest.loadCorporate({ id: corporateId })
        }

        if (corporateStore.corporate?.isPublicInsurance && !env.features.diga) {
          digaNotSupportedCorporateId.value = corporateId
          return
        }

        navigateNext('insuranceSelected')
      },
      onError: (error) => {
        if (error?.code === 'SELECTION_REQUIRED') return
        showGenericError.value = true
      },
    },
  )

  return {
    form,
    fieldAttrs,
    submitAttrs,
    submit,
    showGenericError,
    showDigaNotSupported,
  }
}
